import { useEffect, useMemo } from "react";
import { toast } from "react-toastify";
import { Box, IconButton, Tooltip } from "@mui/material";
import SyncIcon from "@mui/icons-material/Sync";
import { useAppDispatch, useAppSelector } from "app/hooks";
import { selectUserIsAdminOrUserAdmin } from "features/auth/authSlice";
import OrderInfo from "./OrderInfo";
import ChecklistsSection from "./ChecklistsSection";
import OrderProductsSection from "./OrderProductsSection";
import Inspections from "./Inspections";
import WorkDescriptionSection from "./WorkDescriptionSection";
import OrderFilesSection from "./OrderFilesSection";
import PageLayout from "components/PageLayout";
import Breadcrumbs from "features/breadcrumbs/Breadcrumbs";
import OutlinedCard from "components/OutlinedCard";
import Tabs from "components/Tabs";
import { setLastViewedOrder } from "pages/orders/ordersSlice";
import {
  useGetOrderCommentsQuery,
  useGetOrderFilesQuery,
  useSyncToPyramidMutation,
} from "app/api/orders";
import OrderProvider, { useOrderContext } from "context/OrderContext";

const OrderDetails: React.FC = () => {
  const dispatch = useAppDispatch();
  const order = useOrderContext();
  const isAdminOrUserAdmin = useAppSelector(selectUserIsAdminOrUserAdmin);
  const [syncToPyramid, { status: syncStatus }] = useSyncToPyramidMutation();
  const { data: workDescriptionComments } = useGetOrderCommentsQuery(order.ordernr);
  const { data: orderFiles } = useGetOrderFilesQuery(order.ordernr);

  const checklistsCount = order?.checklists?.length || 0;
  const inspectionsCount = order?.customerApprovals?.length ?? 0;
  const productsCount = useMemo(() => {
    const products = order?.productRows?.reduce((acc, row) => acc + row.serials.length, 0) ?? 0;
    const serialEntities =
      order?.checklists?.reduce((acc, checklist) => acc + checklist.serialEntities.length, 0) ?? 0;
    return products + serialEntities;
  }, [order]);

  useEffect(() => {
    dispatch(setLastViewedOrder(order.ordernr));
  }, [dispatch, order]);

  const syncToPyramidAction = () => {
    if (!order) return;

    syncToPyramid(order?.ordernr)
      .unwrap()
      .then(() => {
        toast.success("Ordern synkad till pyramid", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      })
      .catch(() => {
        toast.error("Ordern kunde inte synkas till pyramid", {
          autoClose: 3000,
          hideProgressBar: true,
        });
      });
  };

  return (
    <PageLayout>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Breadcrumbs />

        {isAdminOrUserAdmin && (
          <Tooltip title="Synka order till Pyramid">
            <IconButton onClick={syncToPyramidAction} loading={syncStatus === "pending"}>
              <SyncIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      <OrderProvider order={order}>
        <OrderInfo />

        <OutlinedCard sx={{ p: 0 }}>
          <Tabs
            queryKey="section"
            values={[
              {
                key: "checklists",
                label: "Checklistor",
                badgeCount: checklistsCount,
                content: <ChecklistsSection />,
              },
              {
                key: "products",
                label: "Produkter",
                badgeCount: productsCount,
                content: <OrderProductsSection />,
              },
              {
                key: "inspections",
                label: "Besiktningar",
                badgeCount: inspectionsCount,
                content: <Inspections />,
              },
              {
                key: "workDescription",
                label: "Arbetsbeskrivning",
                badgeCount: workDescriptionComments?.length,
                content: <WorkDescriptionSection />,
              },
              {
                key: "files",
                label: "Filer",
                badgeCount: orderFiles?.length,
                content: <OrderFilesSection />,
              },
            ]}
          />
        </OutlinedCard>
      </OrderProvider>
    </PageLayout>
  );
};

export default OrderDetails;
